import React, { useState, useEffect } from 'react';
import { logger } from '../utils/logger';
import { Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { OptimizedImage } from './OptimizedImage';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Hero = () => {
  const { t: _t } = useTranslation();
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchHeroImages();
  }, []);

  const fetchHeroImages = async () => {
    try {
      const response = await axios.get(`${API}/cms/hero-slides?t=${Date.now()}`);
      const activeSlides = response.data.filter(slide => slide.active);
      const sortedSlides = activeSlides.sort((a, b) => a.order - b.order);
      setImages(sortedSlides.map(slide => slide.image_url));
    } catch (error) {
      logger.error('Error fetching hero images:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div className="h-[600px] bg-white animate-pulse"></div>;
  }

  return (
    <section className="relative bg-gradient-to-br from-white via-white to-white py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Photos Row - Responsive: 1 col mobile, 3 cols desktop */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-12 max-w-7xl mx-auto">
          {images.slice(0, 3).map((image, index) => (
            <div 
              key={image.url || image.src || `hero-img-${index}`} 
              className="relative overflow-hidden shadow-lg"
            >
              {/* Fixed aspect ratio container prevents CLS */}
              <div className="aspect-[3/4] md:aspect-[3/4]">
                <OptimizedImage
                  src={image}
                  alt={`L'ART handgemaakte manden ${index + 1}`}
                  preset="hero"
                  priority={index === 0}
                  fetchPriority={index === 0 ? "high" : undefined}
                  decoding="async"
                  objectFit="cover"
                  className="w-full h-full"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Text Content Below */}
        <div className="text-center max-w-4xl mx-auto">
          <h1 className="font-sweet-sans text-3xl sm:text-4xl font-normal text-earth-brown mb-6 uppercase tracking-wide">
            Handgemaakte manden en interieurdecoratie
          </h1>
          
          <div className="font-sweet-sans text-sm leading-relaxed text-almost-black/80 mb-8 max-w-3xl mx-auto">
            <p>Ben je op zoek naar een stijlvolle opbergmand, broodmandjes met bijpassende placemats, of een decoratief mandje waar je ook je spulletjes in kwijt kunt? Bij L'ART vind je zeker iets dat aansluit bij jouw stijl. Elk stuk wordt gemaakt in ons atelier in Oudenaarde of Brugge. Duurzame materialen, hoogwaardige afwerking en oog voor detail staan daarbij centraal</p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-x-6 md:gap-x-8 gap-y-2 md:gap-y-3 mb-8 md:mb-10">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 md:w-4 md:h-4 text-terra-beige flex-shrink-0" />
              <span className="font-sweet-sans text-xs md:text-sm text-almost-black/80">Handgemaakt in België</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 md:w-4 md:h-4 text-terra-beige flex-shrink-0" />
              <span className="font-sweet-sans text-xs md:text-sm text-almost-black/80">Elk stuk is uniek</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 md:w-4 md:h-4 text-terra-beige flex-shrink-0" />
              <span className="font-sweet-sans text-xs md:text-sm text-almost-black/80">80% gerecycleerd katoen</span>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 md:gap-8 px-4 sm:px-0">
            <Link 
              to="/shop" 
              className="text-earth-brown font-sweet-sans text-sm uppercase tracking-wider underline hover:text-earth-brown/70 transition-colors duration-200 font-bold"
              data-testid="hero-discover-collection"
            >
              Ontdek de collectie
            </Link>
            <button 
              onClick={() => {
                const professionalsSection = document.getElementById('professionals');
                if (professionalsSection) {
                  professionalsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              className="text-earth-brown font-sweet-sans text-sm uppercase tracking-wider underline hover:text-earth-brown/70 transition-colors duration-200 bg-transparent border-0 cursor-pointer font-bold"
              data-testid="hero-professionals"
            >
              Professionals
            </button>
            <Link
              to="/kleuren"
              className="text-earth-brown font-sweet-sans text-sm uppercase tracking-wider underline hover:text-earth-brown/70 transition-colors duration-200 font-bold"
              data-testid="hero-discover-colors"
            >
              Ontdek de kleuren
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
